/**
 * <p-focus-carousel> — Endlos-Karussell mit zentriertem, vergrößertem Aktiv-Bild
 * (Section „Bilder im Fokus"). Ersetzt Hyper-Swiper 1:1:
 * loop, centeredSlides, speed 500 ms, threshold 8 px, Pfeile (Desktop), Punkte (Mobile).
 * slidesPerView/Abstand kommen per CSS (--ifc-spv / gap), JS rechnet nur die Position.
 */
if (!customElements.get('p-focus-carousel')) {
  const SPEED = 500;
  const THRESHOLD = 8;

  class PFocusCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-ifc-viewport]');
      this.track = this.querySelector('[data-ifc-track]');
      if (!this.viewport || !this.track) return;
      this.real = [...this.track.children];
      this.count = this.real.length;
      this.dots = [...this.querySelectorAll('[data-ifc-dot]')];
      if (this.count < 2) {
        this.real[0]?.classList.add('is-active');
        return;
      }

      this.buildClones();
      this.index = this.count;
      this.jump(this.index);

      this.querySelector('[data-ifc-prev]')?.addEventListener('click', () => this.move(-1));
      this.querySelector('[data-ifc-next]')?.addEventListener('click', () => this.move(1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goToReal(i)));

      this.track.addEventListener('transitionend', (event) => {
        if (event.target === this.track && event.propertyName === 'transform') this.normalize();
      });

      this.bindDrag();

      this.resizeObserver = new ResizeObserver(() => this.jump(this.index));
      this.resizeObserver.observe(this.viewport);

      this.onBlockSelect = (event) => {
        const i = this.real.findIndex((slide) => slide.contains(event.target));
        if (i > -1) this.goToReal(i);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    buildClones() {
      const makeClone = (slide) => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        clone.dataset.ifcClone = '';
        clone.querySelectorAll('img').forEach((img) => img.setAttribute('loading', 'lazy'));
        return clone;
      };
      const before = this.real.map(makeClone);
      const after = this.real.map(makeClone);
      this.track.prepend(...before);
      this.track.append(...after);
      this.slides = [...this.track.children];
    }

    /* Subpixel-genau (offsetLeft rundet → Versatz bei 1,4 sichtbaren Bildern) */
    get step() {
      return this.slides[1].getBoundingClientRect().left - this.slides[0].getBoundingClientRect().left;
    }

    offsetFor(index) {
      const slideWidth = this.slides[0].getBoundingClientRect().width;
      return -index * this.step + (this.viewport.getBoundingClientRect().width - slideWidth) / 2;
    }

    setTranslate(x, animate) {
      this.track.style.transition = animate ? `transform ${SPEED}ms ease` : 'none';
      this.track.style.transform = `translate3d(${x}px, 0, 0)`;
      this.currentX = x;
    }

    setActive(index) {
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      const realIndex = (((index - this.count) % this.count) + this.count) % this.count;
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === realIndex);
        if (i === realIndex) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    /* Ohne Animation (auch die Bild-Übergänge) an eine Position springen */
    jump(index) {
      this.index = index;
      this.classList.add('is-jumping');
      this.setActive(index);
      this.setTranslate(this.offsetFor(index), false);
      void this.track.offsetWidth;
      this.classList.remove('is-jumping');
    }

    /* Nach Übergang auf Klon → auf das echte Pendant springen (Endlos-Schleife) */
    normalize() {
      if (this.index < this.count) this.jump(this.index + this.count);
      else if (this.index >= this.count * 2) this.jump(this.index - this.count);
    }

    goTo(index) {
      this.index = index;
      this.setActive(index);
      this.setTranslate(this.offsetFor(index), true);
    }

    move(delta) {
      this.normalize();
      this.goTo(this.index + delta);
    }

    goToReal(realIndex) {
      this.normalize();
      this.goTo(this.count + realIndex);
    }

    bindDrag() {
      let startX = 0;
      let startY = 0;
      let startTime = 0;
      let startTranslate = 0;
      let dragging = false;
      let pointerId = null;
      let locked = false;

      this.viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startY = event.clientY;
        startTime = performance.now();
        dragging = false;
        locked = false;
      });

      this.viewport.addEventListener('pointermove', (event) => {
        if (event.pointerId !== pointerId || locked) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        if (!dragging) {
          if (Math.abs(dx) < THRESHOLD && Math.abs(dy) < THRESHOLD) return;
          if (Math.abs(dy) > Math.abs(dx)) {
            locked = true;
            return;
          }
          dragging = true;
          this.normalize();
          startTranslate = this.offsetFor(this.index);
          startX = event.clientX;
          this.viewport.setPointerCapture(pointerId);
          this.classList.add('is-dragging');
          return;
        }
        this.setTranslate(startTranslate + dx, false);
      });

      const end = (event) => {
        if (event.pointerId !== pointerId) return;
        pointerId = null;
        if (!dragging) return;
        dragging = false;
        this.classList.remove('is-dragging');
        const dx = this.currentX - startTranslate;
        const moved = -dx / this.step;
        const quick = performance.now() - startTime < 300;
        let delta = Math.round(moved);
        if (quick && delta === 0 && Math.abs(dx) > THRESHOLD) delta = moved > 0 ? 1 : -1;
        this.goTo(this.index + delta);
      };
      this.viewport.addEventListener('pointerup', end);
      this.viewport.addEventListener('pointercancel', end);
      this.viewport.addEventListener('dragstart', (event) => event.preventDefault());
    }
  }

  customElements.define('p-focus-carousel', PFocusCarousel);
}
